import React, { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Grid,
    Avatar,
    InputAdornment,
    Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
    Payments,
    History,
    Info,
    Add,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Search,
    Refresh,
    Business,
    LocalHospital,
    CalendarToday,
    AttachMoney,
    Receipt,
    Notes,
    TrendingUp,
    TrendingDown,
} from "@mui/icons-material";
import {
    getAllSupplierPayments,
    getSupplierPaymentSummary,
    recordSupplierPayment
} from "../services/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function SupplierPaymentsPage() {
    const theme = useTheme();
    const { t } = useTranslation();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [summary, setSummary] = useState({
        overdueCount: 0,
        pendingCount: 0,
        totalOwed: 0,
    });

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openPayModal, setOpenPayModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [payFormData, setPayFormData] = useState({
        amount: "",
        paymentMethod: "cash",
        transactionReference: "",
        notes: "",
    });

    const fetchPaymentsData = async () => {
        try {
            setLoading(true);
            setError("");

            const [paymentsRes, summaryRes] = await Promise.all([
                getAllSupplierPayments(),
                getSupplierPaymentSummary()
            ]);

            setPayments(paymentsRes.data.payments || []);
            setSummary(summaryRes.data.summary || {
                overdueCount: 0,
                pendingCount: 0,
                totalOwed: 0,
            });

        } catch (err) {
            console.error("Error fetching supplier payments:", err);
            setError(t('supplierPayments.failedToLoadData'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPaymentsData();
    }, []);

    const handleOpenPayModal = (payment) => {
        setSelectedPayment(payment);
        setPayFormData({
            amount: payment.remainingAmount,
            paymentMethod: "cash",
            transactionReference: "",
            notes: "",
        });
        setOpenPayModal(true);
    };

    const handleRecordPayment = async () => {
        if (!payFormData.amount || payFormData.amount <= 0) {
            toast.error(t('supplierPayments.enterValidAmount'));
            return;
        }

        try {
            await recordSupplierPayment(selectedPayment.id, payFormData);
            toast.success(t('supplierPayments.paymentRecordedSuccess'));
            setOpenPayModal(false);
            fetchPaymentsData();
        } catch (err) {
            const msg = err.response?.data?.message || t('supplierPayments.failedToRecordPayment');
            toast.error(`❌ ${msg}`);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case "paid":
                return <Chip label={t('supplierPayments.statusPaid')} color="success" size="small" icon={<CheckCircle />} />;
            case "overdue":
                return <Chip label={t('supplierPayments.statusOverdue')} color="error" size="small" icon={<ErrorIcon />} />;
            case "partial":
                return <Chip label={t('supplierPayments.statusPartial')} color="warning" size="small" icon={<Warning />} />;
            default:
                return <Chip label={t('supplierPayments.statusPending')} color="info" size="small" icon={<Info />} />;
        }
    };

    const filteredPayments = payments.filter((p) => {
        const matchesSearch =
            p.Supplier?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.Inventory?.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.Inventory?.Medicine?.name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === "all" || p.paymentStatus === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedPayments = filteredPayments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
            {/* Header */}
            <Box
                sx={{
                    textAlign: "center",
                    mb: 6,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                    borderRadius: 4,
                    py: 4,
                    px: 3,
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
            >
                <Payments sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    {t('supplierPayments.title')}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {t('supplierPayments.subtitle')}
                </Typography>
            </Box>

            {/* Summary Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'error.main' }}>
                                <ErrorIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" color="error">
                                    {summary.overdueCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t('supplierPayments.overduePayments')}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'warning.main' }}>
                                <Warning />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" color="warning.dark">
                                    {summary.pendingCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t('supplierPayments.pendingPartialPayments')}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <AttachMoney />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" color="primary">
                                    {t('common.currency') || 'ETB'} {parseFloat(summary.totalOwed).toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t('supplierPayments.totalOutstandingOwed')}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 4, borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label={t('supplierPayments.searchPlaceholder')}
                                fullWidth
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('supplierPayments.status')}</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label={t('supplierPayments.status')}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">{t('supplierPayments.allStatus')}</MenuItem>
                                    <MenuItem value="pending">{t('supplierPayments.pending')}</MenuItem>
                                    <MenuItem value="partial">{t('supplierPayments.partial')}</MenuItem>
                                    <MenuItem value="paid">{t('supplierPayments.paid')}</MenuItem>
                                    <MenuItem value="overdue">{t('supplierPayments.overdue')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                fullWidth
                                onClick={fetchPaymentsData}
                                sx={{ height: '56px' }}
                            >
                                {t('common.refresh')}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Table */}
            <TableContainer 
                component={Paper} 
                sx={{ 
                    borderRadius: 3, 
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                        borderRadius: 4
                    }
                }}
            >
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                        <TableRow>
                            <TableCell><strong>{t('supplierPayments.colSupplierMed')}</strong></TableCell>
                            <TableCell><strong>{t('supplierPayments.colType')}</strong></TableCell>
                            <TableCell align="right"><strong>{t('supplierPayments.colTotalAmount')}</strong></TableCell>
                            <TableCell align="right"><strong>{t('supplierPayments.colPaid')}</strong></TableCell>
                            <TableCell align="right"><strong>{t('supplierPayments.colRemaining')}</strong></TableCell>
                            <TableCell><strong>{t('supplierPayments.colDueDate')}</strong></TableCell>
                            <TableCell><strong>{t('supplierPayments.colStatus')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('common.actions')}</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPayments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <Typography variant="h6" color="textSecondary">{t('supplierPayments.noRecordsFound')}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedPayments.map((p) => (
                                <TableRow key={p.id}>
                                    <TableCell>
                                        <Box>
                                            <Typography fontWeight="bold">{p.Supplier?.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {p.Inventory?.Medicine?.name} ({p.Inventory?.batchNumber})
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={p.paymentType.toUpperCase()}
                                            size="small"
                                            variant="outlined"
                                            color={p.paymentType === 'cash' ? 'default' : 'secondary'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{t('common.currency') || 'ETB'} {parseFloat(p.totalAmount).toFixed(2)}</TableCell>
                                    <TableCell align="right">{parseFloat(p.paidAmount).toFixed(2)}</TableCell>
                                    <TableCell align="right">
                                        <Typography fontWeight="bold" color={p.remainingAmount > 0 ? "error" : "success.main"}>
                                            {parseFloat(p.remainingAmount).toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2">
                                                {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{getStatusChip(p.paymentStatus)}</TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Tooltip title={t('supplierPayments.viewHistory')}>
                                                <IconButton color="info" size="small"><History /></IconButton>
                                            </Tooltip>
                                            {p.remainingAmount > 0 && (
                                                <Tooltip title={t('supplierPayments.recordPayment')}>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleOpenPayModal(p)}
                                                    >
                                                        {t('supplierPayments.payButton')}
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredPayments.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={t('common.rowsPerPage') || "Rows per page:"}
            />

            {/* Record Payment Modal */}
            <Dialog
                open={openPayModal}
                onClose={() => setOpenPayModal(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 3,
                    background: `linear-gradient(to right, ${alpha(theme.palette.success.main, 0.05)}, transparent)`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main' }}>
                        <Payments />
                    </Avatar>
                    <Typography variant="h6" component="div" fontWeight="bold">{t('supplierPayments.recordPaymentTitle')}</Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 2, p: 3 }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 3, border: `1px dashed ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" color="textSecondary">{t('supplierPayments.modalSupplier')}:</Typography>
                            <Typography variant="body1" fontWeight="bold">{selectedPayment?.Supplier?.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary">{t('supplierPayments.modalOutstanding')}:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="error.main">{t('common.currency') || 'ETB'} {selectedPayment?.remainingAmount}</Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label={t('supplierPayments.modalAmountToPay')}
                                type="number"
                                fullWidth
                                value={payFormData.amount}
                                onChange={(e) => setPayFormData({ ...payFormData, amount: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start">{t('common.currency') || 'ETB'}</InputAdornment> }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('supplierPayments.modalMethod')}</InputLabel>
                                <Select
                                    value={payFormData.paymentMethod}
                                    label={t('supplierPayments.modalMethod')}
                                    onChange={(e) => setPayFormData({ ...payFormData, paymentMethod: e.target.value })}
                                >
                                    <MenuItem value="cash">{t('common.cash')}</MenuItem>
                                    <MenuItem value="bank_transfer">{t('supplierPayments.methodBankTransfer')}</MenuItem>
                                    <MenuItem value="check">{t('supplierPayments.methodCheck')}</MenuItem>
                                    <MenuItem value="mobile_money">{t('supplierPayments.methodMobileMoney')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label={t('supplierPayments.modalReference')}
                                fullWidth
                                value={payFormData.transactionReference}
                                onChange={(e) => setPayFormData({ ...payFormData, transactionReference: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label={t('supplierPayments.modalNotes')}
                                fullWidth
                                multiline
                                rows={2}
                                value={payFormData.notes}
                                onChange={(e) => setPayFormData({ ...payFormData, notes: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenPayModal(false)}
                        variant="outlined"
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleRecordPayment}
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.success.dark} 90%)`,
                            boxShadow: '0 3px 15px rgba(46, 125, 50, 0.3)',
                            '&:hover': {
                                boxShadow: '0 5px 20px rgba(46, 125, 50, 0.4)',
                            }
                        }}
                    >
                        {t('supplierPayments.modalConfirmButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
